import { format } from 'date-fns';

interface ServiceLog {
  service_type: string;
  quantity: number | null;
  created_at: string | null;
  client_id: string;
}

interface Client {
  id: string;
  first_name: string;
  last_name: string;
}

const normalizeServiceType = (type: string): string => {
  const lower = type.toLowerCase();
  if (lower === 'breakfast') return 'breakfast';
  if (lower === 'lunch') return 'lunch';
  if (lower === 'dinner') return 'dinner';
  if (lower === 'milk') return 'milk';
  if (lower === 'juice') return 'juice';
  if (lower.includes('metro') || lower.includes('transit')) return 'metro';
  if (lower.includes('laundry') || lower.includes('wash')) return 'laundry';
  if (lower.includes('meal') || lower.includes('food')) return 'meal';
  if (lower.includes('beverage') || lower.includes('drink')) return 'juice';
  return lower;
};

const SERVICE_KEYS = ['metro', 'laundry', 'breakfast', 'lunch', 'dinner', 'milk', 'juice'] as const;
const DISPLAY_NAMES: Record<string, string> = {
  metro: 'Transit', laundry: 'Laundry', breakfast: 'Breakfast',
  lunch: 'Lunch', dinner: 'Dinner', milk: 'Milk', juice: 'Juice',
};

export function printServiceReport(
  logs: ServiceLog[],
  clients: Client[],
  view: string,
  rangeStart: Date,
  rangeEnd: Date
) {
  const countByType = (type: string) =>
    logs.filter(l => normalizeServiceType(l.service_type) === type).reduce((s, l) => s + (l.quantity || 1), 0);

  const totals = SERVICE_KEYS.map(k => ({ name: DISPLAY_NAMES[k], count: countByType(k) }));
  const grandTotal = totals.reduce((s, t) => s + t.count, 0);

  // Per-client breakdown
  const clientMap = new Map<string, Record<string, number>>();
  logs.forEach(log => {
    const key = log.client_id;
    if (!clientMap.has(key)) clientMap.set(key, {});
    const norm = normalizeServiceType(log.service_type);
    const m = clientMap.get(key)!;
    m[norm] = (m[norm] || 0) + (log.quantity || 1);
  });

  const clientRows = Array.from(clientMap.entries()).map(([id, counts]) => {
    const client = clients.find((c: any) => c.id === id);
    const name = client ? `${client.first_name} ${client.last_name}` : 'Unknown';
    const total = Object.values(counts).reduce((s, v) => s + v, 0);
    return { name, counts, total };
  }).sort((a, b) => b.total - a.total);

  const dateLabel = view === 'daily'
    ? format(rangeStart, 'MMMM d, yyyy')
    : `${format(rangeStart, 'MMM d')} – ${format(rangeEnd, 'MMM d, yyyy')}`;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  printWindow.document.write(`<!DOCTYPE html><html><head><title>Service Report</title>
<style>
  body { font-family: Arial, sans-serif; padding: 24px; color: #1a1a1a; }
  h1 { font-size: 20px; margin-bottom: 4px; }
  .subtitle { color: #666; font-size: 14px; margin-bottom: 20px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 13px; }
  th, td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; }
  th { background: #f5f5f5; font-weight: 600; }
  .total-row { font-weight: 700; background: #f0f0f0; }
  @media print { body { padding: 0; } }
</style></head><body>
<h1>Service Report — ${view.charAt(0).toUpperCase() + view.slice(1)}</h1>
<div class="subtitle">${dateLabel}</div>

<h3>Summary</h3>
<table>
  <thead><tr><th>Service</th><th>Count</th></tr></thead>
  <tbody>
    ${totals.map(t => `<tr><td>${t.name}</td><td>${t.count}</td></tr>`).join('')}
    <tr class="total-row"><td>Total</td><td>${grandTotal}</td></tr>
  </tbody>
</table>

<h3>Per-Client Breakdown</h3>
<table>
  <thead><tr><th>Client</th>${SERVICE_KEYS.map(k => `<th>${DISPLAY_NAMES[k]}</th>`).join('')}<th>Total</th></tr></thead>
  <tbody>
    ${clientRows.map(r => `<tr><td>${r.name}</td>${SERVICE_KEYS.map(k => `<td>${r.counts[k] || 0}</td>`).join('')}<td>${r.total}</td></tr>`).join('')}
  </tbody>
</table>

<div style="font-size:11px;color:#999;margin-top:20px;">Generated ${format(new Date(), 'MMM d, yyyy h:mm a')}</div>
</body></html>`);
  printWindow.document.close();
  printWindow.print();
}
